import React, { useState, useRef, useEffect } from 'react';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { getRegions } from '../../lib/regions';
import { ACTIVITY_CATEGORY_FILTER_LABELS } from '../../lib/activityTaxonomy';

const COST_LEVELS = ['Free', '$', '$$', '$$$'];
const DIFFICULTY_LEVELS = ['Easy', 'Moderate', 'Hard'];

const ActivitiesFilterBar = ({ filters, onChange }) => {
  const [open, setOpen] = useState(null);
  const filterRef = useRef(null);
  const { island } = getRuntimeSiteContext();
  const regions = getRegions(island);

  const toggleMulti = (field, value) => {
    const current = filters[field] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    onChange({ ...filters, [field]: updated });
  };

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(null);
    }

    function handleClickOutside(e) {
      if (open && filterRef.current && !filterRef.current.contains(e.target)) {
        setOpen(null);
      }
    }

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <>
      <div ref={filterRef} className="filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search box */}
          <input
            type="text"
            placeholder="Search activities..."
            className="
              w-full px-4 py-2 bg-site-bg rounded border border-white text-white text-sm placeholder-[#E0E4E7] hover:bg-gray-700 transition-colors
              focus:outline-none focus:ring-2 focus:ring-[#63BCE5]"
            value={filters.searchText}
            onChange={e => onChange({ ...filters, searchText: e.target.value })}
          />

          {/* Region dropdown */}
          <div className="relative">
            <button
              className="px-4 py-2 rounded bg-site-bg text-white text-sm border border-white hover:bg-gray-700 transition-colors flex items-center text-sm"
              onClick={() => setOpen(open === 'region' ? null : 'region')}
            >
              Region ▾
            </button>
            {open === 'region' && (
              <div className="absolute mt-2 w-48 max-h-60 overflow-auto bg-site-bg border border-gray-600 rounded shadow-lg z-20">
                {regions.map(r => {
                  const checked = filters.region.includes(r);
                  return (
                    <label key={r} className="flex items-center px-3 py-1 hover:bg-gray-700 cursor-pointer text-white">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMulti('region', r)}
                        className="mr-2"
                      />
                      {r}
                    </label>
                  );
                })}
                <div className="p-2 border-t border-gray-700 text-center">
                  <button className="text-sm text-blue-400 hover:underline" onClick={() => setOpen(null)}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Category dropdown */}
          <div className="relative">
            <button
              className="
                px-4 py-2 rounded bg-site-bg text-white text-sm border border-white hover:bg-gray-700 transition-colors
                flex items-center text-sm
              "
              onClick={() => setOpen(open === 'category' ? null : 'category')}
            >
              Category ▾
            </button>

            {open === 'category' && (
              <div className="absolute mt-2 w-56 max-h-60 overflow-auto bg-site-bg border border-gray-600 rounded shadow-lg z-20">
                {ACTIVITY_CATEGORY_FILTER_LABELS.map(c => {
                  const checked = filters.category.includes(c);
                  return (
                    <label
                      key={c}
                      className="flex items-center px-3 py-1 hover:bg-gray-700 cursor-pointer text-white"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMulti('category', c)}
                        className="mr-2"
                      />
                      {c}
                    </label>
                  );
                })}

                <div className="p-2 border-t border-gray-700 text-center">
                  <button className="text-sm text-blue-400 hover:underline" onClick={() => setOpen(null)}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Cost dropdown */}
          <div className="relative">
            <button
              className="
                px-4 py-2 rounded bg-site-bg text-white text-sm border border-white hover:bg-gray-700 transition-colors
                flex items-center text-sm
              "
              onClick={() => setOpen(open === 'cost' ? null : 'cost')}
            >
              Cost ▾
            </button>

            {open === 'cost' && (
              <div className="
                  absolute mt-2 w-24 max-h-60 overflow-auto
                  bg-site-bg border border-gray-600 rounded shadow-lg z-20
                ">
                {COST_LEVELS.map(c => {
                  const checked = filters.cost.includes(c);
                  return (
                    <label
                      key={c}
                      className="flex items-center px-3 py-1 hover:bg-gray-700 cursor-pointer text-white"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMulti('cost', c)}
                        className="mr-2"
                      />
                      {c}
                    </label>
                  );
                })}

                <div className="p-2 border-t border-gray-700 text-center">
                  <button className="text-sm text-blue-400 hover:underline" onClick={() => setOpen(null)}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Difficulty dropdown */}
          <div className="relative">
            <button
              className="
                px-4 py-2 rounded bg-site-bg text-white text-sm border border-white hover:bg-gray-700 transition-colors
                flex items-center text-sm
              "
              onClick={() => setOpen(open === 'difficulty' ? null : 'difficulty')}
            >
              Difficulty ▾
            </button>

            {open === 'difficulty' && (
              <div className="
                  absolute mt-2 w-32 max-h-60 overflow-auto
                  bg-site-bg border border-gray-600 rounded shadow-lg z-20
                ">
                {DIFFICULTY_LEVELS.map(d => {
                  const checked = filters.difficulty.includes(d);
                  return (
                    <label
                      key={d}
                      className="flex items-center px-3 py-1 hover:bg-gray-700 cursor-pointer text-white"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMulti('difficulty', d)}
                        className="mr-2"
                      />
                      {d}
                    </label>
                  );
                })}

                <div className="p-2 border-t border-gray-700 text-center">
                  <button className="text-sm text-blue-400 hover:underline" onClick={() => setOpen(null)}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected-filters summary */}
        {(filters.region.length > 0 ||
          filters.category.length > 0 ||
          filters.cost.length > 0 ||
          filters.difficulty.length > 0 ||
          filters.searchText) && (
          <div className="mt-2 flex flex-wrap gap-2 text-white">

            {/* Region */}
            {filters.region.map(r => (
              <span
                key={`sel-region-${r}`}
                className="flex items-center bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm"
              >
                {r}
                <button
                  onClick={() => toggleMulti('region', r)}
                  className="ml-1 font-bold hover:text-red-400"
                >
                  ×
                </button>
              </span>
            ))}

            {/* Category */}
            {filters.category.map(c => (
              <span
                key={`sel-category-${c}`}
                className="flex items-center bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm"
              >
                {c}
                <button
                  onClick={() => toggleMulti('category', c)}
                  className="ml-1 font-bold hover:text-red-400"
                >
                  ×
                </button>
              </span>
            ))}

            {/* Cost */}
            {filters.cost.map(c => (
              <span
                key={`sel-cost-${c}`}
                className="flex items-center bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm"
              >
                {c}
                <button
                  onClick={() => toggleMulti('cost', c)}
                  className="ml-1 font-bold hover:text-red-400"
                >
                  ×
                </button>
              </span>
            ))}

            {/* Difficulty */}
            {filters.difficulty.map(d => (
              <span
                key={`sel-difficulty-${d}`}
                className="flex items-center bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm"
              >
                {d}
                <button
                  onClick={() => toggleMulti('difficulty', d)}
                  className="ml-1 font-bold hover:text-red-400"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

      </div>
      <button
        className="text-sm hover:underline"
        onClick={() => onChange({
          region: [],
          category: [],
          cost: [],
          difficulty: [],
          searchText: '',
        })}
      >
        Clear all filters
      </button>
    </>
  );
};

export default ActivitiesFilterBar;
